import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/i18n';

type AuthMode = 'login' | 'signup' | 'reset';

export default function Login() {
  const { user, signIn, signUp, signInWithGoogle, resetPassword, loading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [mode, setMode] = React.useState<AuthMode>(
    location.pathname === '/register' ? 'signup' : 'login'
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [error, setError] = React.useState('');
  const [resetSent, setResetSent] = React.useState(false);

  if (user && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  function getFirebaseErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found': return t('auth.errorUserNotFound');
      case 'auth/wrong-password': return t('auth.errorWrongPassword');
      case 'auth/email-already-in-use': return t('auth.errorEmailInUse');
      case 'auth/weak-password': return t('auth.errorWeakPassword');
      case 'auth/invalid-email': return t('auth.errorInvalidEmail');
      case 'auth/too-many-requests': return t('auth.errorTooManyRequests');
      case 'auth/invalid-credential': return t('auth.errorWrongPassword');
      default: return t('auth.errorGeneric');
    }
  }

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await signIn(email, password);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err?.code || ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t('auth.errorPasswordMismatch'));
      return;
    }
    setIsSubmitting(true);
    try {
      await signUp(email, password, companyName || email.split('@')[0]);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err?.code || ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err?.code || ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err?.code || ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">C</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">ClawCustomer</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {mode === 'reset' ? t('auth.resetPassword') : t('auth.welcomeBack')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {mode === 'reset' ? t('auth.resetPasswordDesc') : t('auth.signInSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {resetSent && mode === 'reset' && (
            <div className="mb-4 rounded-md bg-green-50 p-3">
              <p className="text-sm text-green-800">{t('auth.resetEmailSent')}</p>
            </div>
          )}

          {mode === 'reset' ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">{t('auth.email')}</label>
                <input id="reset-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                {isSubmitting ? t('common.loading') : t('auth.sendResetEmail')}
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); setResetSent(false); }} className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500">
                {t('auth.backToLogin')}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={mode === 'login' ? handleEmailSignIn : handleEmailSignUp} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('auth.email')}</label>
                  <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">{t('auth.password')}</label>
                  <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>

                {mode === 'signup' && (
                  <>
                    <div>
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">{t('auth.confirmPassword')}</label>
                      <input id="confirm-password" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                      <label htmlFor="company-name" className="block text-sm font-medium text-gray-700">{t('auth.companyName')}</label>
                      <input id="company-name" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </div>
                  </>
                )}

                {mode === 'login' && (
                  <div className="flex justify-end">
                    <button type="button" onClick={() => { setMode('reset'); setError(''); }} className="text-sm text-indigo-600 hover:text-indigo-500">
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                )}

                <button type="submit" disabled={isSubmitting} className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                  {isSubmitting ? t('common.loading') : (mode === 'login' ? t('auth.signIn') : t('auth.signUp'))}
                </button>
              </form>

              <div className="mt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-2 text-gray-500">{t('common.or')}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isSubmitting}
                  className="mt-4 w-full inline-flex justify-center items-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="ml-3">{t('auth.continueWithGoogle')}</span>
                </button>
              </div>

              <div className="mt-4 text-center">
                {mode === 'login' ? (
                  <p className="text-sm text-gray-600">
                    {t('auth.noAccount')}{' '}
                    <button type="button" onClick={() => { setMode('signup'); setError(''); }} className="text-indigo-600 hover:text-indigo-500 font-medium">{t('auth.signUp')}</button>
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">
                    {t('auth.hasAccount')}{' '}
                    <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-indigo-600 hover:text-indigo-500 font-medium">{t('auth.signIn')}</button>
                  </p>
                )}
              </div>
            </>
          )}

          <p className="mt-6 text-center text-xs text-gray-500">
            {t('auth.agreeToTerms')}{' '}
            <a href="https://www.clawcustomer.com/terms" className="text-indigo-600 hover:text-indigo-500">{t('auth.termsOfService')}</a>
            {' '}{t('auth.and')}{' '}
            <a href="https://www.clawcustomer.com/privacy" className="text-indigo-600 hover:text-indigo-500">{t('auth.privacyPolicy')}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
